import React from 'react';
import { Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppLauncherStore } from '../../store/appLauncherStore';

export interface AppTool {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  path: string;
  badge?: 'New' | 'Beta' | 'Popular' | 'Premium';
  category: string;
}

interface ToolWidgetProps {
  hubId: string;
  tool: AppTool;
}

export function ToolWidget({ hubId, tool }: ToolWidgetProps) {
  const navigate = useNavigate();
  const { pinnedTools, togglePin } = useAppLauncherStore();
  
  const isPinned = (pinnedTools[hubId] || []).includes(tool.id);
  const Icon = tool.icon;

  return (
    <article
      onClick={() => navigate(tool.path)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          navigate(tool.path);
        }
      }}
      role="link"
      tabIndex={0}
      className="group relative flex h-full cursor-pointer flex-col rounded-lg border bg-card p-4 transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:scale-105 transition-transform">
          <Icon className="h-5 w-5" />
        </div>
        
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              togglePin(hubId, tool.id);
            }}
            className="p-1.5 text-muted-foreground hover:text-warning rounded-md hover:bg-background transition-colors"
            title={isPinned ? "Unpin" : "Pin to Favorites"}
            aria-label={isPinned ? 'Unpin from favorites' : 'Pin to favorites'}
          >
            <Star className={`h-4 w-4 ${isPinned ? 'fill-warning text-warning' : ''}`} />
          </button>
        </div>
        
        {/* Always show pin if pinned and not hovering */}
        {isPinned && (
          <div className="absolute top-4 right-4 group-hover:hidden text-warning">
            <Star className="h-4 w-4 fill-warning" />
          </div>
        )}
      </div>

      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold tracking-tight text-foreground text-sm">{tool.name}</h3>
          {tool.badge && (
            <span className={`px-1.5 py-0.5 rounded text-xs font-semibold
              ${tool.badge === 'New' ? 'bg-primary/10 text-primary' : 
                tool.badge === 'Beta' ? 'bg-primary/10 text-primary' : 
                tool.badge === 'Popular' ? 'bg-success/10 text-success' :
                'bg-warning/10 text-warning'}`}
            >
              {tool.badge}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
          {tool.description}
        </p>
      </div>
    </article>
  );
}
